import { ExpensablesDataCollectionQuery } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getSpendingAlertsDisplay } from 'modules/expenses/lib/expensables/spendingAlertsHelper'
import { getStatusColor } from 'modules/expenses/lib/expensables/statusColor'
import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

type Expensable =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

interface Props {
  isMyExpenses?: boolean
  locale: string
}

export const useExpensesDataCollection = ({ isMyExpenses = false, locale }: Props) => {
  return {
    sortings: {
      effectiveOn: {
        label: i18n.t('expenses_v2.list.header.date'),
      },
      status: {
        label: i18n.t('expenses_v2.list.header.status'),
      },
      amount: {
        label: i18n.t('expenses_v2.list.header.amount'),
      },
    },
    defaultSortings: {
      field: 'effectiveOn' as const,
      order: 'desc' as const,
    },
    columns: [
      ...(isMyExpenses
        ? [
            {
              label: i18n.t('expenses_v2.list.header.merchant_name'),
              render: (item: Expensable) => {
                let label
                if (item.mileage) {
                  label = i18n.t('expenses_v2.expense.mileage_expense')
                } else if (item.perDiem) {
                  label = i18n.t('expenses_v2.expense.per_diem_expense')
                } else label = item.expense?.userMerchant

                return {
                  type: 'text' as const,
                  value: label,
                }
              },
            },
          ]
        : [
            {
              label: i18n.t('expenses_v2.list.header.owner'),
              render: (item: Expensable) => ({
                type: 'person' as const,
                value: {
                  firstName: item.owner?.access.fullName,
                  lastName: '',
                  src: item.owner?.access.avatar ? buildUrl(item.owner.access.avatar) : undefined,
                },
              }),
            },
          ]),
      {
        label: i18n.t('expenses_v2.list.header.status'),
        sorting: 'status' as const,
        render: (item: Expensable) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(item.status),
            status: getStatusColor(item.status),
          },
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.date'),
        sorting: 'effectiveOn' as const,
        render: (item: Expensable) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(item.effectiveOn),
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.amount'),
        sorting: 'amount' as const,
        render: (item: Expensable) => ({
          type: 'text' as const,
          value: amountWithCurrency(Number(item.amount), item.currency, locale),
        }),
      },
      {
        label: i18n.t('expenses_v2.list.header.category'),
        render: (item: Expensable) => {
          const expensable = item.expense || item.mileage || item.perDiem
          return {
            type: 'tag' as const,
            value: {
              label:
                expensable?.category?.name ?? i18n.t('expenses_v2.expense.category.uncategorized'),
            },
          }
        },
      },
      {
        label: i18n.t('expenses_v2.list.header.spending_alerts'),
        render: (item: Expensable) => {
          const alertDisplay = getSpendingAlertsDisplay(
            item.spendingAlertsTarget,
            item.spendingAlertsSource
          )

          return {
            type: 'status' as const,
            value: {
              label: alertDisplay?.label,
              status: alertDisplay?.color,
            },
          }
        },
      },
    ],
  }
}
